<template>
    <Headify :data="headMeta" />
    <div class="min-h-screen bg-white">
        <NavMain :key="route.name" />
        <div class="flex flex-2 md:pl-64">
            <div class="fixed w-full max-w-7xl px-2 sm:px-4 md:pt-4">
                <div class="space-y-6 py-6">
                    <div class="sm:col-span-2">
                        <div class="www w-2/5 px-1 py-2 sm:p-4">
                            <label
                                for="snippet-name"
                                class="block text-sm font-medium text-gray-700"
                            >
                                {{ t('snippetName') }}
                            </label>
                            <input
                                id="snippet-name"
                                v-model="snippet.name"
                                type="text"
                                name="first-name"
                                autocomplete="off"
                                class="mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-hidden sm:text-sm"
                                :class="
                                    (snippet.name?.length ?? 0) === 0
                                        ? 'border-red-300 focus:border-blue-500 focus:ring-blue-500'
                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                "
                            />
                        </div>

                        <div class="w-2/5 p-4">
                            <label
                                for="type"
                                class="block text-sm font-medium text-gray-700"
                            >
                                {{ t('snippetType') }}
                            </label>
                            <select
                                id="type"
                                v-model="snippet.type"
                                name="type"
                                class="mt-1 block w-full rounded-md bg-white px-3 py-2 shadow-sm focus:outline-hidden sm:text-sm"
                                :class="
                                    (snippet.type?.length ?? 0) === 0
                                        ? 'border-red-300 focus:border-blue-500 focus:ring-blue-500'
                                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                                "
                            >
                                <option
                                    v-for="r in config.snippet.types"
                                    :key="r"
                                >
                                    {{ r.length === 0 ? 'NONE' : r }}
                                </option>
                            </select>
                        </div>

                        <div class="flex flex-2 flex-row space-x-2 px-1 py-3 sm:p-4">
                            <div class="w-1/2">
                                <p class="block text-sm font-medium text-gray-700">
                                    {{ t('snippetSource') }}
                                </p>

                                <CodeMirror
                                    ref="cm"
                                    :key="editorKey"
                                    v-model="content"
                                    :extensions="modules"
                                    :lang="ext"
                                    :wrap="true"
                                    class="rounded-md border p-2"
                                />

                                <button
                                    class="mr-4 inline-flex justify-center rounded-md border border-transparent bg-gray-200 px-2 py-2 text-base font-medium text-gray-600 shadow-sm hover:bg-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-hidden"
                                    @click="formatHtml()"
                                >
                                    {{ t('buttonFormatSnippet') }}
                                </button>
                            </div>

                            <div
                                v-if="content"
                                class="w-1/2"
                            >
                                <p class="block text-sm font-medium text-gray-700">
                                    {{ t('snippetPreview') }}
                                </p>
                                <div
                                    class="rounded-md border p-2 break-words"
                                    v-html="content"
                                ></div>
                            </div>
                        </div>

                        <div class="px-1 py-2 text-right sm:p-4">
                            <ActionButton
                                :value="t('buttonBack')"
                                state="neutral"
                                :icon="MdiChevronLeft"
                                @click="router.go(-1)"
                            />

                            <ActionButton
                                :value="t('buttonSave')"
                                state="primary"
                                :icon="MdiContentSave"
                                :is-disabled="saveForm === false"
                                @click="saveSnippetData(snippetid, snippet, saving, content)"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Sidebar :action="replaceInEditor" />
        </div>
    </div>

    <Spinner
        v-if="saving.active"
        type="complex"
        :color="`text-blue-600`"
        :color2="`text-blue-400`"
        :text-color="`text-gray-200`"
        :overlay="true"
        :speed="1000"
        :dots="true"
        :dot-char="'.'"
        :dot-dual="false"
        :dot-reverse="false"
        :text="saving.text"
        :inline="false"
        :pulse="true"
    />
</template>
<script setup>
import { reactive, ref, watch, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getSnippet, saveSnippet } from '../composables/useSnippets';
import Sidebar from '../components/Sidebar.vue';
import Headify from '../components/Headify.vue';
import Spinner from '../ui/Spinner.vue';
import ActionButton from '../ui/ActionButton.vue';
import { notify } from 'notiwind';
import NavMain from '../ui/NavMain.vue';
import navs from '../composables/navs';
import { t } from '../composables/i18n';
import { MdiChevronLeft, MdiContentSave } from 'materialdesignicons-vue3/icons/';
import { html_beautify } from 'js-beautify';
import { getRndKey } from '../composables/utils';

const route = useRoute(),
    snippetid = route.params.snippetid || '0',
    router = useRouter(),
    snippet = reactive(await getSnippet(snippetid)),
    saving = reactive({
        active: false,
        text: `${t('loading')}`,
    }),
    config = inject('config'),
    saveForm = ref(false),
    content = ref(snippet.source ? JSON.parse(snippet.source).content : ''),
    cm = ref(null),
    //ext = [html()],
    editorKey = ref(getRndKey()),
    headMeta = navs().metaFind(route.name);

watch(snippet, (snippet) => {
    validateSnippetData(snippet);
});

const validateSnippetData = (snippet) => {
    saveForm.value = (snippet.name?.length ?? 0) > 0 && (snippet.type?.length ?? 0) > 0;
};

const saveSnippetData = async (snippetid, snippet, saving, content) => {
    saving.active = true;
    snippet.source = JSON.stringify({ content: content });

    const res = await saveSnippet(snippetid, snippet, saving);

    if (res !== false) {
        saving.active = false;
        notify(
            {
                group: 'success',
                title: 'Done',
                text: `Snippet ${snippet.name} was successfully saved`,
            },
            2500,
        );
    } else {
        notify(
            {
                group: 'error',
                title: 'Error',
                text: `Error saving snippet ${snippet.name}`,
            },
            5000,
        );
        saving.active = false;
    }
};

validateSnippetData(snippet);

const html_beautifyConfig = {
    indent_size: '4',
    indent_char: ' ',
    max_preserve_newlines: '5',
    preserve_newlines: true,
    keep_array_indentation: false,
    break_chained_methods: false,
    indent_scripts: 'normal',
    brace_style: 'collapse',
    space_before_conditional: true,
    unescape_strings: false,
    jslint_happy: false,
    end_with_newline: false,
    wrap_line_length: '160',
    indent_inner_html: true,
    comma_first: false,
    e4x: false,
    indent_empty_lines: false,
};

function replaceInEditor(text) {
    cm.value.replaceRange(text, cm.value.getCursor(), cm.value.getCursor());
    editorKey.value = getRndKey();
}

function formatHtml() {
    if (content.value) {
        try {
            content.value = html_beautify(content.value, html_beautifyConfig);
        } catch (e) {
            console.log('Error while formatting html');
            console.log(e);
        }
    }
}
</script>
